import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import courseProgress from '../../utils/courseProgress';
import sanitizeHtml from '../../utils/sanitizeHtml';
import {
  emailValidationMessage,
  normalizeEmail,
} from '../../utils/emailValidation';
import { ScrollLink } from '../ScrollLink';
import QuickAccessModal from './QuickAccessModal';
import '../../styles/ModuloCurso.css';

const getModuleDisplayTitle = (title) => {
  const normalizedTitle = String(title || '').trim();
  const withoutPrefix = normalizedTitle.replace(
    /^(?:(?:\d+\s*[º°o]?\s*m[oó]dulo)|(?:m[oó]dulo\s*\d+))\s*[:\-–—]?\s*/i,
    ''
  );

  return withoutPrefix || normalizedTitle;
};

const ModuloCurso = () => {
  const { cursoId, moduloId } = useParams();
  const [curso, setCurso] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [completedModules, setCompletedModules] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem('token')));
  const [showQuickAccess, setShowQuickAccess] = useState(false);
  const [pendingDestination, setPendingDestination] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [quickAccessError, setQuickAccessError] = useState('');
  const [savingAccess, setSavingAccess] = useState(false);
  const [activePreviewToken, setActivePreviewToken] = useState('');
  const [autoPromptShown, setAutoPromptShown] = useState(false);
  const mainContentRef = useRef(null);
  const activeModuleRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const courseResponse = await apiService.cursos.get(cursoId);
        setCurso(courseResponse.data);

        let modulesData;
        let moduleData;
        let progress = courseProgress.getCompletedModules(cursoId);

        if (isAuthenticated) {
          const [modulesResponse, moduleResponse, progressResponse] = await Promise.all([
            apiService.modulos.listByCourse(cursoId),
            apiService.modulos.get(cursoId, moduloId),
            apiService.progress.getCourse(cursoId),
          ]);
          modulesData = modulesResponse.data;
          moduleData = moduleResponse.data;
          progress = [...new Set([
            ...progress,
            ...(progressResponse.data.completed_module_ids || []),
          ].map(Number))];
        } else {
          modulesData = courseResponse.data.modulos || [];
          const previewKey = `course_preview_${cursoId}_${moduloId}`;
          let previewToken = sessionStorage.getItem(previewKey);

          const startPreview = async () => {
            try {
              const previewResponse = await apiService.preview.start(cursoId, moduloId);
              return previewResponse.data.preview_token;
            } catch (previewError) {
              if (previewError.response?.status === 409 && previewError.response.data?.active_module_id) {
                sessionStorage.setItem(
                  `course_preview_pending_${cursoId}`,
                  `/curso/${cursoId}/modulo/${moduloId}`
                );
                navigate(`/curso/${cursoId}/modulo/${previewError.response.data.active_module_id}`, { replace: true });
                return null;
              }
              throw previewError;
            }
          };

          if (!previewToken) {
            previewToken = await startPreview();
            if (!previewToken) return;
            sessionStorage.setItem(previewKey, previewToken);
          }

          try {
            const moduleResponse = await apiService.preview.getModule(cursoId, moduloId, previewToken);
            moduleData = moduleResponse.data;
          } catch (previewError) {
            if (previewError.response?.status !== 401) throw previewError;
            previewToken = await startPreview();
            if (!previewToken) return;
            sessionStorage.setItem(previewKey, previewToken);
            const moduleResponse = await apiService.preview.getModule(cursoId, moduloId, previewToken);
            moduleData = moduleResponse.data;
          }
          setActivePreviewToken(previewToken);
        }

        setModulos(modulesData);
        setModulo(moduleData);
        courseProgress.saveProgress(cursoId, progress);
        courseProgress.markModuleAsCompleted(cursoId, parseInt(moduloId));

        const pendingPreviewDestination = sessionStorage.getItem(`course_preview_pending_${cursoId}`);
        if (!isAuthenticated && pendingPreviewDestination) {
          sessionStorage.removeItem(`course_preview_pending_${cursoId}`);
          setPendingDestination(pendingPreviewDestination);
          setAutoPromptShown(true);
          setShowQuickAccess(true);
        }

        if (isAuthenticated) {
          await apiService.progress.update({
            curso_id: Number(cursoId),
            modulo_id: Number(moduloId),
          });
        }

      } catch (err) {

        setError('Não foi possível carregar o conteúdo. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cursoId, moduloId, isAuthenticated, navigate]);

  useEffect(() => {
    setAutoPromptShown(false);
  }, [moduloId]);

  useEffect(() => {
    if (isAuthenticated || !modulo || loading || showQuickAccess || autoPromptShown) return undefined;

    const timer = window.setTimeout(() => {
      setPendingDestination(null);
      setAutoPromptShown(true);
      setShowQuickAccess(true);
    }, 20000);

    return () => window.clearTimeout(timer);
  }, [autoPromptShown, isAuthenticated, loading, modulo, moduloId, showQuickAccess]);

  // Listener para atualizações de progresso
  useEffect(() => {
    const handleProgressUpdate = (event) => {
      if (event.detail.courseId === cursoId) {
        setCompletedModules(event.detail.completedModules);
      }
    };

    window.addEventListener('courseProgressUpdated', handleProgressUpdate);
    
    return () => {
      window.removeEventListener('courseProgressUpdated', handleProgressUpdate);
    };
  }, [cursoId]);

  useEffect(() => {
    if (mainContentRef.current?.scrollTo) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (activeModuleRef.current?.scrollIntoView) {
      activeModuleRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [moduloId]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [sidebarOpen]);

  const getCurrentModuleIndex = () => {
    return modulos.findIndex(m => m.id === parseInt(moduloId));
  };

  const getNextModule = () => {
    const currentIndex = getCurrentModuleIndex();
    return currentIndex < modulos.length - 1 ? modulos[currentIndex + 1] : null;
  };

  const getPreviousModule = () => {
    const currentIndex = getCurrentModuleIndex();
    return currentIndex > 0 ? modulos[currentIndex - 1] : null;
  };

  const markCurrentModuleCompleted = async () => {
    courseProgress.markModuleAsCompleted(cursoId, parseInt(moduloId));

    if (isAuthenticated) {
      await apiService.progress.update({
        curso_id: Number(cursoId),
        modulo_id: Number(moduloId),
      });
    }
  };

  const handleProtectedNavigation = async (destination) => {
    try {
      await markCurrentModuleCompleted();
    } catch (_) {
      // O progresso local permanece salvo e será sincronizado no próximo acesso.
    }

    if (!isAuthenticated) {
      setPendingDestination(destination);
      setQuickAccessError('');
      setAutoPromptShown(true);
      setShowQuickAccess(true);
      setSidebarOpen(false);
      return;
    }

    navigate(destination);
    setSidebarOpen(false);
  };

  const handleModuleNavigation = (targetModuleId) => {
    if (parseInt(targetModuleId) === parseInt(moduloId)) {
      setSidebarOpen(false);
      return;
    }

    handleProtectedNavigation(`/curso/${cursoId}/modulo/${targetModuleId}`);
  };

  const completeQuickAccess = (response, completedModuleIds) => {
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data));
    courseProgress.saveProgress(cursoId, [
      ...new Set([
        ...completedModuleIds,
        ...(response.data.completed_module_ids || []),
      ].map(Number)),
    ]);
    setIsAuthenticated(true);
    setShowQuickAccess(false);
    window.dispatchEvent(new CustomEvent('authChanged'));

    if (pendingDestination) {
      navigate(pendingDestination);
      setPendingDestination(null);
    }
  };

  const handleQuickGoogleCredential = async (credential) => {
    setQuickAccessError('');
    setSavingAccess(true);
    const completedModuleIds = [...new Set([
      ...courseProgress.getCompletedModules(cursoId),
      Number(moduloId),
    ].map(Number))];

    try {
      const response = await apiService.auth.google(credential, {
        curso_id: Number(cursoId),
        modulo_id: Number(moduloId),
        preview_token: activePreviewToken || sessionStorage.getItem(`course_preview_${cursoId}_${moduloId}`),
      });
      completeQuickAccess(response, completedModuleIds);
    } catch (requestError) {
      setQuickAccessError(
        requestError.response?.data?.error || 'Não foi possível continuar com o Google. Tente novamente.'
      );
    } finally {
      setSavingAccess(false);
    }
  };

  const handleQuickAccessSubmit = async (event) => {
    event.preventDefault();
    setQuickAccessError('');

    const emailError = emailValidationMessage(email);
    if (emailError) {
      setQuickAccessError(emailError);
      return;
    }

    if (password.length < 6 || password.length > 72) {
      setQuickAccessError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (!confirmPassword || password !== confirmPassword) {
      setQuickAccessError('As senhas informadas não coincidem.');
      return;
    }

    try {
      setSavingAccess(true);
      const completedModuleIds = [...new Set([
        ...courseProgress.getCompletedModules(cursoId),
        Number(moduloId),
      ].map(Number))];
      const response = await apiService.auth.quickStart({
        email: normalizeEmail(email),
        password,
        password_confirmation: confirmPassword,
        curso_id: Number(cursoId),
        modulo_id: Number(moduloId),
        preview_token: activePreviewToken || sessionStorage.getItem(`course_preview_${cursoId}_${moduloId}`),
      });
      completeQuickAccess(response, completedModuleIds);
    } catch (requestError) {
      const responseData = requestError.response?.data;
      const firstValidationError = responseData?.errors
        ? Object.values(responseData.errors).flat()[0]
        : null;
      setQuickAccessError(firstValidationError || responseData?.error || 'Não foi possível salvar seu acesso. Tente novamente.');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleQuickAccessDismiss = () => {
    if (savingAccess) return;
    setShowQuickAccess(false);
    setPendingDestination(null);
    setQuickAccessError('');
  };

  // Verificar se um módulo está completo
  const isModuleCompleted = (moduleId) => {
    return completedModules.includes(parseInt(moduleId));
  };

  // Mostrar loading apenas para dados do curso, não para progresso
  if (loading) {
    return (
      <div className="modulo-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando módulo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modulo-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Erro ao carregar módulo</h2>
          <p>Não conseguimos carregar o conteúdo deste módulo. Tente novamente.</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!curso || !modulo) {
    return (
      <div className="modulo-page">
        <div className="error-container">
          <div className="error-icon">📚</div>
          <h2>Módulo não encontrado</h2>
          <p>O módulo que você está procurando não existe ou foi removido.</p>
          <ScrollLink to={`/curso/${cursoId}/estudo`} className="back-link">
            Voltar ao Curso
          </ScrollLink>
        </div>
      </div>
    );
  }

  const currentIndex = getCurrentModuleIndex();
  const nextModule = getNextModule();
  const previousModule = getPreviousModule();
  const completedCount = modulos.filter((item) => isModuleCompleted(item.id)).length;
  const progressPercentage = modulos.length
    ? Math.round((completedCount / modulos.length) * 100)
    : 0;
  const moduleDuration = modulo.duration || '~ 15 minutos';

  return (
    <div className="modulo-page">
      {/* Mobile Header */}
      <header className="mobile-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-expanded={sidebarOpen}
          aria-controls="course-modules-sidebar"
          aria-label={sidebarOpen ? 'Fechar menu de módulos' : 'Abrir menu de módulos'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="course-info">
          <h1 className="course-title">{curso.title}</h1>
          <span className="module-counter">{currentIndex + 1} de {modulos.length}</span>
        </div>
      </header>

      <div className="content-layout">
        {/* Sidebar */}
        <aside id="course-modules-sidebar" className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="course-info">
              <span className="sidebar-eyebrow">Módulos do curso</span>
              <h2 className="course-title">{curso.title}</h2>
            </div>
            <button
              type="button"
              className="sidebar-close"
              onClick={() => setSidebarOpen(false)}
              aria-label="Fechar menu de módulos"
            >
              <span>Fechar</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <div className="course-progress-panel">
            <div className="course-progress-copy">
              <span>Seu progresso</span>
              <span>{completedCount} de {modulos.length} concluídos</span>
            </div>
            <div
              className="course-progress-track"
              role="progressbar"
              aria-label="Progresso do curso"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={progressPercentage}
            >
              <span style={{ width: `${progressPercentage}%` }}></span>
            </div>
            <strong>{progressPercentage}%</strong>
          </div>

          <div className="modules-list">
            <button
              type="button"
              className="take-exam take-exam-inline"
              onClick={() => handleProtectedNavigation(`/curso/${cursoId}/prova`)}
            >
              <span className="exam-card-copy">
                <span className="exam-card-icon" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 4H6.5C5.67 4 5 4.67 5 5.5V20H19V5.5C19 4.67 18.33 4 17.5 4H16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="8" y="2.5" width="8" height="4" rx="1.2" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M8 11H16M8 15H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </span>
                <span>
                  <strong>Se sente preparado para a prova?</strong>
                  <small>Você pode fazer a prova sempre que quiser.</small>
                </span>
              </span>
              <span className="exam-card-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Fazer a prova
              </span>
            </button>

            {modulos.map((mod, index) => (
              <button
                key={mod.id}
                ref={mod.id === parseInt(moduloId) ? activeModuleRef : null}
                className={`module-item ${mod.id === parseInt(moduloId) ? 'active' : ''} ${isModuleCompleted(mod.id) ? 'completed' : ''}`}
                onClick={() => handleModuleNavigation(mod.id)}
              >
                <div className="module-number" aria-hidden="true">
                  {isModuleCompleted(mod.id) ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : mod.id === parseInt(moduloId) ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="5,3 19,12 5,21" fill="currentColor"/>
                    </svg>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <div className="module-info">
                  <span className="module-kicker">{index + 1}º Módulo</span>
                  <span className="module-title">{getModuleDisplayTitle(mod.title)}</span>
                </div>
                <svg className="module-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
          </div>

          <div className="sidebar-actions">
            <ScrollLink to={`/curso/${cursoId}/estudo`} className="back-to-course">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Voltar ao Curso
            </ScrollLink>
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content" ref={mainContentRef}>
          {/* Breadcrumb */}
          <nav className="breadcrumb">
            <Link to={isAuthenticated ? '/meus-cursos' : `/curso/${cursoId}`} className="breadcrumb-item">
              {isAuthenticated ? 'Meus Cursos' : 'Detalhes do curso'}
            </Link>
            <span className="breadcrumb-separator">›</span>
            <Link to={`/curso/${cursoId}/estudo`} className="breadcrumb-item">{curso.title}</Link>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">{modulo.title}</span>
          </nav>

          <div className="module-back-link-wrap">
            <Link to={`/curso/${cursoId}/estudo`} className="module-back-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Voltar para os módulos
            </Link>
          </div>

          <header className="module-hero">
            <div className="module-hero-meta">
              <span className="module-position">Módulo {currentIndex + 1} de {modulos.length}</span>
              <span className="module-status completed">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <circle cx="10" cy="10" r="9" fill="currentColor"/>
                  <path d="M6 10.2L8.5 12.6L14 7.4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Concluído
              </span>
            </div>
            <h1>{modulo.title}</h1>
            <div className="module-reading-time">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M12 7V12L15.5 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Tempo estimado de leitura: <strong>{moduleDuration}</strong></span>
            </div>
          </header>

          {/* Module Content */}
          <section className="module-content" aria-label={`Conteúdo do módulo ${currentIndex + 1}`}>
            <div className="content-wrapper">
              {modulo.content ? (
                <div 
                  className="rich-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(modulo.content) }}
                />
              ) : (
                <div className="simple-content">
                  <p>Este módulo ainda não possui conteúdo disponível.</p>
                  <p>Em breve será adicionado material de estudo para este tópico.</p>
                </div>
              )}

              <aside className="study-tip">
                <span className="study-tip-icon" aria-hidden="true">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18H15M10 22H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    <path d="M8.3 15.5C6.87 14.4 6 12.67 6 10.75C6 7.57 8.69 5 12 5C15.31 5 18 7.57 18 10.75C18 12.67 17.13 14.4 15.7 15.5C15.1 15.96 14.75 16.55 14.75 17.25H9.25C9.25 16.55 8.9 15.96 8.3 15.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                    <path d="M12 1V2.5M3.5 4.5L4.7 5.5M20.5 4.5L19.3 5.5M2 11H3.5M20.5 11H22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </span>
                <div>
                  <h2>Dica</h2>
                  <p>Durante a leitura, anote os principais conceitos. Eles serão úteis nos próximos módulos e também na prova.</p>
                </div>
              </aside>
            </div>
          </section>

          {/* Navigation Footer */}
          <footer className="module-navigation">
            <div className="nav-actions">
              {previousModule ? (
                <button
                  type="button"
                  onClick={() => handleModuleNavigation(previousModule.id)}
                  className="nav-button prev"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="nav-info">
                    <span className="nav-label">Anterior</span>
                    <span className="nav-title">{getModuleDisplayTitle(previousModule.title)}</span>
                  </div>
                </button>
              ) : (
                <ScrollLink to={`/curso/${cursoId}/estudo`} className="nav-button prev modules-return">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="nav-info">
                    <span className="nav-label">Voltar</span>
                    <span className="nav-title">Módulos do curso</span>
                  </div>
                </ScrollLink>
              )}

              {nextModule ? (
                <button
                  type="button"
                  onClick={() => handleModuleNavigation(nextModule.id)}
                  className="nav-button next"
                >
                  <div className="nav-info">
                    <span className="nav-label">Próximo</span>
                    <span className="nav-title">{getModuleDisplayTitle(nextModule.title)}</span>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  className="nav-button exam"
                  onClick={() => handleProtectedNavigation(`/curso/${cursoId}/prova`)}
                >
                  <div className="nav-info">
                    <span className="nav-label">Finalizar</span>
                    <span className="nav-title">Fazer Prova</span>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3.89543 5 5 3.89543 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </footer>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <QuickAccessModal
        open={showQuickAccess}
        courseTitle={curso.title}
        email={email}
        password={password}
        confirmPassword={confirmPassword}
        error={quickAccessError}
        loading={savingAccess}
        requiresAccount={Boolean(pendingDestination)}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
        onGoogleCredential={handleQuickGoogleCredential}
        onGoogleError={setQuickAccessError}
        onSubmit={handleQuickAccessSubmit}
        onDismiss={handleQuickAccessDismiss}
      />
    </div>
  );
};

export default ModuloCurso;
